import { IHome } from "@src/components/Home";
import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import ClientOnly from "@src/components/ClientOnly";
import { motion, AnimatePresence } from "framer-motion";
import Floater from "../Floater";
import Footer from "../Footer";
import Vendors from "../Vendors";
import Parts from "../Parts";
import FAQ from "../FAQ";
import Recommendations from "../Recommendations";
import Backstory from "../Backstory";
import Masonry from "../Masonry";
import Hero from "../Hero";
import AsoEbi from "../AsoEbiLink";
import Registry from "../Registry";
import MonetHeader, { HeaderNavItem } from "../Header";
import { useEventParts } from "@src/hooks/useEventParts";
import { useHome } from "@src/hooks/useHome";
import { parseEventDate } from "@src/utils/dateFormatter";
import WeddingParty from "../WeddingParty";
import {
  Event_PreferencesQuery,
  Event_PreferencesQueryVariables,
  EventBySlugQuery,
  EventPartsQuery,
} from "@src/@types/api.d";
import { GetEventPreferences } from "@src/graphql/queries";
import styles from "./styles.module.scss";

interface ComponentItem {
  id: string;
  label: string;
  component: React.ReactNode;
}

type IntroTextPart = {
  text: string;
  className?: string;
};

type EventSlugParts = NonNullable<
  EventBySlugQuery["eventBySlug"]
>["event_parts"];

const getFirstValidDate = (dates: Array<string | null | undefined>) =>
  dates.find((date) => !!date && !!parseEventDate(date)) ?? null;

const getPublicEventPartDates = (eventParts?: EventSlugParts | null) =>
  (eventParts ?? []).map((part) => part?.time).filter(Boolean);

const getEventPartDates = (eventPartsData?: EventPartsQuery) =>
  (eventPartsData?.eventParts ?? []).map((part) => part.time);

const splitNames = (name?: string | null) => {
  if (!name) return ["", ""];

  const ampersandParts = name.split(/\s*&\s*/);
  if (ampersandParts.length >= 2) {
    return [ampersandParts[0], ampersandParts.slice(1).join(" & ")];
  }

  const andParts = name.split(/\s+and\s+/i);
  if (andParts.length >= 2) {
    return [andParts[0], andParts.slice(1).join(" and ")];
  }

  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return [words[0], words.slice(1).join(" ")];
  }

  return [name, ""];
};

const TypewriterLine: React.FC<{
  as?: "p" | "h1" | "div";
  parts: IntroTextPart[];
  className: string;
  delay?: number;
}> = ({ as = "p", parts, className, delay = 0 }) => {
  const MotionTag = motion[as];
  let charIndex = 0;

  return (
    <MotionTag
      className={className}
      aria-label={parts.map((p) => p.text).join("")}
    >
      {parts.map((part, partIndex) => (
        <span
          className={`${styles["intro-part"]} ${part.className ?? ""}`}
          key={`${part.text}-${partIndex}`}
        >
          {Array.from(part.text).map((char, index) => {
            const currentIndex = charIndex;
            charIndex += 1;

            return (
              <motion.span
                key={`${char}-${partIndex}-${index}`}
                aria-hidden="true"
                className={styles["intro-char"]}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: delay + currentIndex * 0.045,
                  duration: 0.34,
                  ease: [0.16, 1, 0.3, 1],
                }}
              >
                {char === " " ? "\u00A0" : char}
              </motion.span>
            );
          })}
        </span>
      ))}
    </MotionTag>
  );
};

const HomeCT2: React.FC<IHome> = ({
  slug,
  eventBySlugData,
  eventId,
  eventPartsData,
  backStoryData,
  eventVendorData,
  albumDetailsData,
  heroImageData,
  hasWallet,
  getEventStreamersData,
  liveSchedules,
  eventUuid,
  weddingPartyData,
  faqData,
  recommendationData,
  inviteResult: initialInviteResult,
}) => {
  const [viewMode, setViewMode] = useState<"intro" | "hero" | "components">(
    "intro"
  );
  const [activeComponentIndex, setActiveComponentIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState<"left" | "right">(
    "right"
  );

  const {
    accessToken,
    inviteResult,
    getAsoebiData,
    isCashGiftSetup,
    isInvitedToEvent,
  } = useHome({
    slug,
    eventBySlugData,
    eventId,
    eventPartsData,
    backStoryData,
    eventVendorData,
    albumDetailsData,
    heroImageData,
    hasWallet,
    getEventStreamersData,
    liveSchedules,
    eventUuid,
    weddingPartyData,
    faqData,
    recommendationData,
    inviteResult: initialInviteResult,
  });

  const { data: eventPreferencesData } = useQuery<
    Event_PreferencesQuery,
    Event_PreferencesQueryVariables
  >(GetEventPreferences, {
    variables: { eventId: eventUuid as string },
    skip: !eventUuid,
  });

  const shouldShowFaq = eventPreferencesData?.event_preferences?.faq ?? false;
  const shouldShowRecommendations =
    eventPreferencesData?.event_preferences?.recommendations ?? false;
  const { parts, hasEventParts } = useEventParts({
    eventPartsData,
    liveSchedules,
    eventTimezone: eventBySlugData?.eventBySlug?.eventTimezone,
    isInvitedToEvent,
  });

  useEffect(() => {
    const introTimer = window.setTimeout(() => {
      setViewMode("hero");
    }, 5000);

    return () => window.clearTimeout(introTimer);
  }, []);

  // Build available components array
  const availableComponents = useMemo<ComponentItem[]>(() => {
    const components: ComponentItem[] = [];

    // Backstory
    if ((backStoryData?.getBackStory?.length ?? 0) > 0) {
      components.push({
        id: "backstory",
        label: "Backstory",
        component: (
          <Backstory eventId={eventId} backStoryData={backStoryData} />
        ),
      });
    }

    // Parts (Events)
    if (hasEventParts) {
      components.push({
        id: "parts",
        label: "Events",
        component: (
          <Parts
            liveSchedules={liveSchedules}
            eventId={eventId}
            parts={parts}
            eventPartsData={eventPartsData}
            eventTimezone={eventBySlugData?.eventBySlug?.eventTimezone}
            albumDetailsData={albumDetailsData}
          />
        ),
      });
    }

    // FAQ
    if (shouldShowFaq && (faqData?.getFAQsByEvent?.data?.length ?? 0) > 0) {
      components.push({
        id: "faq",
        label: "FAQ",
        component: faqData ? (
          <FAQ
            eventId={eventId}
            faqData={faqData}
            albumDetailsData={albumDetailsData}
          />
        ) : null,
      });
    }

    // Recommendations
    if (
      shouldShowRecommendations &&
      (recommendationData?.getEventRecommendationLists?.length ?? 0) > 0
    ) {
      components.push({
        id: "recommendations",
        label: "Recommendations",
        component: (
          <Recommendations
            eventId={eventId}
            recommendationData={
              recommendationData as NonNullable<typeof recommendationData>
            }
          />
        ),
      });
    }

    // Photobook
    if ((albumDetailsData?.getMedia?.length ?? 0) > 0) {
      components.push({
        id: "masonry",
        label: "Photobook",
        component: (
          <Masonry
            eventSlug={slug}
            eventId={eventId}
            albumDetailsData={albumDetailsData}
          />
        ),
      });
    }

    // WeddingParty
    if ((weddingPartyData?.getWeddingParty?.length ?? 0) > 0) {
      components.push({
        id: "weddingparty",
        label: "Wedding Party",
        component: (
          <WeddingParty eventId={eventId} weddingPartyData={weddingPartyData} />
        ),
      });
    }

    // AsoEbi
    components.push({
      id: "asoebi",
      label: "Asoebi",
      component: (
        <AsoEbi
          eventUuid={eventUuid}
          eventId={eventId}
          showAsoebi={true}
          showCashGift={!!accessToken && isCashGiftSetup}
          isCashGiftSetup={isCashGiftSetup}
          availableGiftPaymentCurrencies={
            eventBySlugData?.eventBySlug?.available_gift_payment_currencies ??
            []
          }
          albumDetailsData={albumDetailsData}
          heroImageData={heroImageData}
          eventBySlugData={eventBySlugData}
        />
      ),
    });

    // Registry
    components.push({
      id: "registry",
      label: "Registry",
      component: <Registry eventId={`${eventUuid}`} />,
    });

    // Vendors
    if ((eventVendorData?.eventVendor?.length ?? 0) > 0) {
      components.push({
        id: "vendors",
        label: "Vendors",
        component: (
          <Vendors eventId={eventId} eventVendorData={eventVendorData} />
        ),
      });
    }

    return components;
  }, [
    eventBySlugData,
    heroImageData,
    albumDetailsData,
    eventPartsData,
    backStoryData,
    eventId,
    hasEventParts,
    liveSchedules,
    parts,
    shouldShowFaq,
    faqData,
    shouldShowRecommendations,
    recommendationData,
    weddingPartyData,
    eventVendorData,
    accessToken,
    slug,
    eventUuid,
    isCashGiftSetup,
  ]);

  const handleHeroNext = () => {
    setActiveComponentIndex(0);
    setViewMode("components");
    setSlideDirection("right");
  };

  const handleCoverTabClick = () => {
    setViewMode("hero");
    setSlideDirection("left");
  };

  const handleNavClick = (id: string) => {
    if (id === "hero") {
      handleCoverTabClick();
      return;
    }

    const nextIndex = availableComponents.findIndex(
      (component) => component.id === id
    );

    if (nextIndex < 0) return;

    setSlideDirection(nextIndex > activeComponentIndex ? "right" : "left");
    setActiveComponentIndex(nextIndex);
    setViewMode("components");
  };

  const currentComponent = availableComponents[activeComponentIndex];
  const [introFirstName, introSecondName] = splitNames(
    eventBySlugData?.eventBySlug?.name
  );
  const resolvedEventDate = getFirstValidDate([
    eventBySlugData?.eventBySlug?.date,
    ...getPublicEventPartDates(eventBySlugData?.eventBySlug?.event_parts),
    ...parts.map((part) => part.time),
    ...getEventPartDates(eventPartsData),
  ]);
  const parsedIntroDate = parseEventDate(resolvedEventDate);
  const introDate = parsedIntroDate
    ? parsedIntroDate.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : "Our special day";
  const introNameParts: IntroTextPart[] = introSecondName
    ? [
        { text: introFirstName },
        { text: " & ", className: styles["intro-title-amp"] },
        { text: introSecondName },
      ]
    : [
        {
          text:
            introFirstName || eventBySlugData?.eventBySlug?.name || "Welcome",
        },
      ];
  const navItems: HeaderNavItem[] = useMemo(
    () => [
      { id: "hero", label: "Home" },
      ...availableComponents.map((component) => ({
        id: component.id,
        label: component.label,
      })),
    ],
    [availableComponents]
  );
  const activeNavId =
    viewMode === "components" && currentComponent
      ? currentComponent.id
      : "hero";

  const heroVariants = {
    initial: { opacity: 0, y: 50 },
    animate: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
    exit: { opacity: 0, x: -100, transition: { duration: 0.4 } },
  };

  const componentsVariants = {
    initial: { opacity: 0, x: 100 },
    animate: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const componentExitVariants = {
    exit: {
      opacity: 0,
      x: slideDirection === "left" ? 100 : -100,
      transition: { duration: 0.3 },
    },
  };

  const componentEnterVariants = {
    initial: {
      opacity: 0,
      x: slideDirection === "left" ? -100 : 100,
    },
    animate: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.4, ease: "easeOut" },
    },
  };

  return (
    <>
      <ClientOnly>
        <Floater
          accessToken={accessToken}
          eventId={eventId}
          eventUuid={eventUuid}
          slugData={eventBySlugData}
          hasWallet={hasWallet}
          getEventStreamersData={getEventStreamersData}
          liveSchedules={liveSchedules}
          access_control={
            eventBySlugData?.eventBySlug?.event_preference?.access_control ??
            false
          }
          qr_code={inviteResult?.signupByInviteLink?.invitation?.qr_code ?? ""}
          getAsoebiData={getAsoebiData}
          admits={inviteResult?.signupByInviteLink?.invitation?.admits ?? 1}
          invitation={inviteResult?.signupByInviteLink?.invitation}
          isInvitedToEvent={isInvitedToEvent}
        />

        <AnimatePresence>
          {viewMode === "intro" ? (
            <motion.div
              key="intro-view"
              className={styles["intro-container"]}
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, transition: { duration: 0.7 } }}
            >
              <div className={styles["intro-content"]}>
                <motion.div
                  className={styles["intro-line"]}
                  initial={{ width: 0, opacity: 0 }}
                  animate={{
                    width: "min(220px, 48vw)",
                    opacity: 1,
                    transition: { duration: 0.8 },
                  }}
                />
                <TypewriterLine
                  parts={[{ text: "A celebration of love" }]}
                  className={styles["intro-pre"]}
                  delay={0.55}
                />
                <TypewriterLine
                  as="h1"
                  parts={introNameParts}
                  className={styles["intro-title"]}
                  delay={1.55}
                />
                <motion.div
                  className={styles["intro-meta-row"]}
                  initial={{ opacity: 1 }}
                  animate={{ opacity: 1 }}
                >
                  <span className={styles["intro-meta-line"]} />
                  <TypewriterLine
                    as="div"
                    parts={[{ text: introDate }]}
                    className={styles["intro-meta-text"]}
                    delay={3.2}
                  />
                  <span className={styles["intro-meta-line"]} />
                </motion.div>
                {eventBySlugData?.eventBySlug?.hash_tag && (
                  <TypewriterLine
                    parts={[{ text: eventBySlugData.eventBySlug.hash_tag }]}
                    className={styles["intro-tag"]}
                    delay={4}
                  />
                )}
                <motion.div
                  className={styles["intro-progress"]}
                  initial={{ scaleX: 0 }}
                  animate={{
                    scaleX: 1,
                    transition: { duration: 5, ease: "linear" },
                  }}
                />
              </div>
            </motion.div>
          ) : viewMode === "hero" ? (
            <motion.div
              key="hero-view"
              variants={heroVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className={styles["hero-container"]}
            >
              <Hero
                heroImageData={heroImageData}
                eventId={eventId}
                slugData={eventBySlugData}
                albumDetailsData={albumDetailsData}
                getEventStreamersData={getEventStreamersData}
                liveSchedules={liveSchedules}
                accessToken={accessToken}
                eventUuid={eventUuid}
                eventDate={resolvedEventDate}
                eventPartsData={eventPartsData}
                getAsoebiData={getAsoebiData}
                admits={
                  inviteResult?.signupByInviteLink?.invitation?.admits ?? 1
                }
                invitation={inviteResult?.signupByInviteLink?.invitation}
                isInvitedToEvent={isInvitedToEvent}
                qrCode={
                  inviteResult?.signupByInviteLink?.invitation?.qr_code ?? ""
                }
                navItems={navItems}
                activeNavId={activeNavId}
                onNavigate={handleNavClick}
                openInvitationModal={
                  availableComponents.length > 0 ? handleHeroNext : undefined
                }
              />
            </motion.div>
          ) : (
            <motion.div
              key="components-view"
              variants={componentsVariants}
              initial="initial"
              animate="animate"
              className={styles["components-container"]}
            >
              <MonetHeader
                slugData={eventBySlugData}
                heroImageData={heroImageData}
                navItems={navItems}
                activeId={activeNavId}
                onNavigate={handleNavClick}
              />

              {/* Active component */}
              <AnimatePresence>
                {currentComponent && (
                  <motion.div
                    key={currentComponent.id}
                    variants={componentEnterVariants}
                    initial="initial"
                    animate="animate"
                    exit={componentExitVariants.exit}
                    className={styles["component-wrapper"]}
                  >
                    {currentComponent.component}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Footer */}
              <Footer slug={eventBySlugData} />
            </motion.div>
          )}
        </AnimatePresence>
      </ClientOnly>
    </>
  );
};

export default HomeCT2;
